import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { ChapterRail, ChapterNav } from "@/components/WorkNav";
import { Reveal } from "../components/Reveal";
import { Logo } from "../components/Logo";

const ID = "/work/idfc";
const MAROON = "#7c1122";

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/10">
    <div className="mx-auto max-w-[1500px] px-5 sm:px-8 h-16 flex items-center justify-between">
      <Logo imgClassName="h-9 sm:h-10" />
      <div className="flex items-center gap-4 sm:gap-6">
        <Link to="/work" data-testid="idfc-back-work-top" className="text-[11px] uppercase tracking-[0.25em] text-neutral-400 hover:text-white transition-colors">← Work</Link>
        <Link to="/#gateways" data-testid="idfc-back-explore-top" className="text-[11px] uppercase tracking-[0.25em] text-neutral-400 hover:text-white transition-colors hidden sm:inline">Explore</Link>
      </div>
    </div>
  </header>
);

const Eyebrow = ({ children, testid }) => (
  <span data-testid={testid} className="text-[11px] sm:text-xs uppercase tracking-[0.35em] text-[#e62429] font-bold">{children}</span>
);

const Chip = ({ src, alt, className = "" }) => (
  <div className={`bg-white p-5 sm:p-7 border border-white/10 flex items-center justify-center ${className}`}>
    <img src={src} alt={alt} loading="lazy" className="max-h-full w-auto object-contain" />
  </div>
);

const SPORTS = ["Football", "Cricket", "Badminton", "Carrom", "Pickleball"];
const CITIES = ["Mumbai","Pune","Ahmedabad","Jaipur","Delhi","Chandigarh","Hyderabad","Bengaluru","Chennai","Kolkata"];
const CYCLE = ["Design", "Operate", "Review", "Evolve"];
const JOURNEY = [
  ["01", "Win the mandate", "Competitive empanelment"],
  ["02", "Own the calendar", "Year-long programme"],
  ["03", "Solve across the bank", "Business-specific briefs"],
];

export default function Idfc() {
  const { scrollYProgress } = useScroll();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="idfc-page" className="bg-[#0a0a0a] text-white min-h-screen">
      <motion.div style={{ scaleX: scrollYProgress }} className="fixed top-0 left-0 right-0 h-[2px] bg-[#e62429] origin-left z-[60]" />
      <Header />
      <ChapterRail currentKey="idfc" />

      {/* OPENER */}
      <section data-testid="idfc-opener" className="min-h-[90svh] flex items-end border-b border-white/10">
        <div className="mx-auto max-w-[1500px] w-full px-5 sm:px-8 pb-16 sm:pb-24 pt-36">
          <Reveal>
            <Eyebrow>Work · Client 03</Eyebrow>
            <div className="mt-5 bg-white inline-flex p-3 sm:p-4">
              <img src={`${ID}/idfc-logo.png`} alt="IDFC First Bank" className="h-8 sm:h-11 w-auto object-contain" />
            </div>
            <h1 className="mt-7 font-display uppercase text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-tight">
              IDFC First Bank
            </h1>
            <p className="mt-6 text-lg sm:text-2xl text-neutral-300 max-w-2xl">Responsibility that expanded after the mandate was won.</p>
          </Reveal>
        </div>
      </section>

      {/* 2021 MANDATE */}
      <section data-testid="idfc-mandate" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <Eyebrow>2021</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95]">A mandate we had to win.</h2>
            <dl className="mt-8 space-y-4 max-w-md">
              {[["Process","Competitive empanelment"],["Reference","Deutsche Bank"],["First assignment","Football, Mumbai"]].map(([k,v]) => (
                <div key={k} className="flex justify-between gap-6 border-b border-white/10 pb-3">
                  <dt className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-bold">{k}</dt>
                  <dd className="text-base sm:text-lg text-neutral-200 text-right">{v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
          <Reveal delay={0.1} className="flex items-center">
            <blockquote className="border-l-2 border-[#e62429] pl-6">
              <p className="font-display uppercase text-white text-3xl sm:text-4xl leading-tight">The relationship began with a bid.</p>
              <p className="mt-2 font-display uppercase text-[#e62429] text-3xl sm:text-4xl leading-tight">The ownership came afterwards.</p>
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* FIRST ASSIGNMENT */}
      <section data-testid="idfc-firstassignment" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>First assignment</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-none">It started with football.</h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-x-16 gap-y-3 text-lg sm:text-2xl text-neutral-300">
              <span>One tournament.</span>
              <span>One opportunity to demonstrate what we could do.</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EVENT -> CALENDAR */}
      <section data-testid="idfc-calendar" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>From an event to a calendar</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95] max-w-4xl">
              Oneworld of Sports became the sole agency operating the Fit &amp; Happy sporting calendar.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
            <Reveal><div className="border border-white/12 p-8"><p className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-bold">Before</p><p className="mt-3 font-display uppercase text-white text-3xl">One event</p></div></Reveal>
            <Reveal delay={0.08}><div className="border border-[#e62429] p-8 bg-[#e62429]/[0.06]"><p className="text-xs uppercase tracking-[0.25em] text-[#e62429] font-bold">After</p><p className="mt-3 font-display uppercase text-white text-3xl">Year-long programme</p></div></Reveal>
          </div>
          <Reveal className="mt-6"><p className="text-base sm:text-lg text-neutral-400 max-w-2xl">Not simply more events — the move from delivering an assignment to owning a sporting rhythm.</p></Reveal>
        </div>
      </section>

      {/* PROGRAMME DESIGN */}
      <section data-testid="idfc-design" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6">
            <Eyebrow>Programme design</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95]">Start with a blank sheet.</h2>
            <ul className="mt-8 space-y-3">
              {["What sports?","Which cities?","What formats?","When?"].map((q) => (
                <li key={q} className="font-display uppercase text-neutral-300 text-2xl sm:text-3xl border-b border-white/[0.07] pb-3">{q}</li>
              ))}
            </ul>
            <p className="mt-8 font-display uppercase text-[#e62429] text-2xl sm:text-3xl">A year designed before it is delivered.</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <img src={`${ID}/cal-2025.webp`} alt="Fit & Happy @ IDFC First — Calendar 2025" loading="lazy" className="w-full h-auto border border-white/10" />
          </Reveal>
        </div>
      </section>

      {/* OPERATING CYCLE */}
      <section data-testid="idfc-cycle" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Operating cycle</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-3xl sm:text-5xl leading-none">A recurring loop — then begin again.</h2>
          </Reveal>
          <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-4">
            {CYCLE.map((step, i) => (
              <Reveal key={step} delay={i * 0.06}>
                <div className="flex items-center gap-3">
                  <span className="font-display uppercase text-white text-2xl sm:text-4xl">{step}</span>
                  <span className="text-[#e62429] text-2xl sm:text-3xl">{i < CYCLE.length - 1 ? "→" : "↻"}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FIT & HAPPY — SPORTS */}
      <section data-testid="idfc-sports" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6">
            <Eyebrow>Fit &amp; Happy</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95]">One calendar. Many sports.</h2>
            <div className="mt-8 flex flex-wrap gap-x-4 gap-y-2">
              {SPORTS.map((s, i) => (
                <span key={s} className="font-display uppercase text-neutral-200 text-2xl sm:text-3xl">
                  {s}{i < SPORTS.length - 1 && <span className="text-neutral-700 ml-4">/</span>}
                </span>
              ))}
            </div>
            <p className="mt-8 text-base sm:text-lg text-neutral-400 max-w-lg">The contents of one programme — not five separate case studies.</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <img src={`${ID}/cal-2026.webp`} alt="Fit & Happy @ IDFC First — Calendar 2026-27" loading="lazy" className="w-full h-auto border border-white/10" />
          </Reveal>
        </div>
      </section>

      {/* NATIONAL FOOTPRINT */}
      <section data-testid="idfc-footprint" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <Eyebrow>National footprint</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95]">One programme. Multiple cities.</h2>
            <ul className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-2">
              {CITIES.map((c) => (<li key={c} className="text-lg sm:text-xl text-neutral-300 border-b border-white/[0.07] py-2">{c}</li>))}
            </ul>
            <p className="mt-8 font-display uppercase text-[#e62429] text-xl sm:text-2xl">One calendar travelling across the organisation.</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="bg-neutral-50 p-6 border border-white/10 max-w-[380px] mx-auto">
              <img src={`${ID}/map.png`} alt="IDFC First national footprint" loading="lazy" className="w-full h-auto object-contain" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRAVEL INSIDE THE BANK */}
      <section data-testid="idfc-spread" className="border-b border-white/10">
        <div className="py-24 sm:py-32" style={{ backgroundColor: MAROON }}>
          <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.35em] text-white/70 font-bold">Organisational spread</p>
              <h2 className="mt-6 font-display uppercase text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.9] max-w-4xl">
                The relationship began to travel inside the bank.
              </h2>
              <p className="mt-8 text-lg sm:text-xl text-white/80 max-w-2xl">
                The central programme stayed intact — while businesses, functions and employee groups began bringing their own briefs and objectives.
              </p>
            </Reveal>
          </div>
        </div>

        {/* MULTIPLE BRIEFS */}
        <div className="py-20 sm:py-28">
          <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
            <Reveal><Eyebrow>One relationship. Multiple briefs.</Eyebrow></Reveal>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <Reveal>
                <Chip src={`${ID}/sportathon.jpg`} alt="IDFC First Sport-A-Thon" className="h-64" />
                <p className="mt-4 font-display uppercase text-white text-xl">Sport-A-Thon</p>
                <p className="text-sm text-neutral-500 uppercase tracking-[0.2em]">Assets / Liabilities</p>
              </Reveal>
              <Reveal delay={0.08}>
                <Chip src={`${ID}/ffl.jpg`} alt="First Forward League" className="h-64" />
                <p className="mt-4 font-display uppercase text-white text-xl">First Forward League</p>
                <p className="text-sm text-neutral-500 uppercase tracking-[0.2em]">Tolls / FASTag</p>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="h-64 border border-white/12 flex items-center justify-center p-8">
                  <p className="font-display uppercase text-white text-3xl text-center leading-tight">Audit<br />+ Finance</p>
                </div>
                <p className="mt-4 font-display uppercase text-white text-xl">Audit + Finance</p>
                <p className="text-sm text-neutral-500 uppercase tracking-[0.2em]">Specific employee groups</p>
              </Reveal>
            </div>
          </div>
        </div>

        {/* TWO OPERATING LAYERS */}
        <div className="border-t border-white/10 py-20 sm:py-28">
          <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
            <Reveal><Eyebrow>Two operating layers — running together</Eyebrow></Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              <Reveal>
                <div className="border border-white/12 p-8 sm:p-10 h-full">
                  <span className="font-display text-neutral-600 text-2xl">01</span>
                  <p className="mt-4 font-display uppercase text-white text-3xl sm:text-4xl">Organisation-wide</p>
                  <p className="mt-3 text-lg text-neutral-400">One bank-wide rhythm.</p>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="border border-white/12 p-8 sm:p-10 h-full">
                  <span className="font-display text-neutral-600 text-2xl">02</span>
                  <p className="mt-4 font-display uppercase text-white text-3xl sm:text-4xl">Business-specific</p>
                  <p className="mt-3 text-lg text-neutral-400">Multiple bespoke briefs.</p>
                </div>
              </Reveal>
            </div>
            <Reveal className="mt-6"><p className="text-base sm:text-lg text-neutral-400">Two levels operating at the same time.</p></Reveal>
          </div>
        </div>
      </section>

      {/* FIRST FORWARD LEAGUE */}
      <section data-testid="idfc-ffl" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <Eyebrow>First Forward League · Tolls / FASTag</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-5xl leading-[0.95] max-w-xl">
              A sporting property built around a specific business —
            </h2>
            <p className="mt-4 text-lg sm:text-xl text-neutral-300">not simply an event added to the central calendar.</p>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 text-neutral-400 text-sm uppercase tracking-[0.2em]">
              <span>Different audience</span><span className="text-neutral-700">·</span>
              <span>Different objective</span><span className="text-neutral-700">·</span>
              <span>Same operating relationship</span>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="space-y-5">
            <Chip src={`${ID}/ffl-partner.png`} alt="First Forward Partner League" className="h-40" />
          </Reveal>
        </div>
      </section>

      {/* HUMAN MOMENT */}
      <section data-testid="idfc-human" className="relative isolate min-h-[80svh] flex items-end overflow-hidden border-b border-white/10">
        <img src={`${ID}/vaidyanathan.webp`} alt="V. Vaidyanathan with players at the First Forward League" className="absolute inset-0 z-0 h-full w-full object-cover object-[center_22%]" />
        <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/20 to-transparent" />
        <div className="relative z-10 mx-auto max-w-[1500px] w-full px-5 sm:px-8 pb-14">
          <Reveal>
            <p className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95] drop-shadow-lg">
              Same field. Same bat. Same game.
            </p>
            <p className="mt-5 text-lg sm:text-2xl text-neutral-200 max-w-lg">For a while, hierarchy simply left the field.</p>
          </Reveal>
        </div>
      </section>

      {/* THE JOURNEY */}
      <section data-testid="idfc-journey" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal><Eyebrow>The journey · 2021 → today</Eyebrow></Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-white/12">
            {JOURNEY.map(([n, t, s], i) => (
              <Reveal key={n} delay={i * 0.08}>
                <div className="border-b md:border-b-0 md:border-r border-white/12 p-8 sm:p-10 h-full last:border-r-0">
                  <span className="font-display text-[#e62429] text-2xl">{n}</span>
                  <p className="mt-4 font-display uppercase text-white text-2xl sm:text-3xl leading-tight">{t}</p>
                  <p className="mt-3 text-base text-neutral-400">{s}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSE */}
      <section data-testid="idfc-close" className="py-24 sm:py-32 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 text-center">
          <Reveal>
            <Eyebrow>IDFC First Bank · 2021 → today</Eyebrow>
            <h2 className="mt-6 font-display uppercase text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.9]">Trust through ownership.</h2>
            <p className="mt-8 text-lg sm:text-xl text-neutral-300 max-w-3xl mx-auto">
              From competitive empanelment to helping shape how sport lives across IDFC FIRST Bank.
            </p>
          </Reveal>
        </div>
      </section>

      <ChapterNav currentKey="idfc" />

      {/* Route back */}
      <section className="py-16">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-14">
          <Link to="/work" data-testid="idfc-back-work-bottom" className="group inline-flex items-center gap-3 font-display uppercase text-2xl sm:text-3xl text-white">
            <ArrowLeft className="h-6 w-6 text-[#e62429] group-hover:-translate-x-1 transition-transform" />
            Back to Work
          </Link>
          <Link to="/#gateways" data-testid="idfc-back-explore-bottom" className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-neutral-400 hover:text-white transition-colors">
            Back to Explore
            <ArrowUpRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      </section>
    </div>
  );
}
